import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, DIRECTIONS, toLanguage } from '@msarat/shared';

import { TRANSLATIONS } from './translations.js';

/**
 * Language and direction.
 *
 * The preference is resolved in this order, most specific first:
 *
 *   1. the `msarat_lang` cookie   — this device, chosen deliberately
 *   2. the signed-in parent's profile — follows them to any device
 *   3. the browser's own language     — a sensible first guess
 *   4. Arabic                          — the product's primary audience
 *
 * The cookie wins over the profile on purpose: someone who just switched
 * language on this device means it for this device, and having the server
 * override that on the next page load would feel broken. Changing it while
 * signed in also writes it to the profile, so the two converge.
 */

const COOKIE = 'msarat_lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const LanguageContext = createContext(null);

function readCookie() {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]*)`));
  return match ? toLanguage(decodeURIComponent(match[1])) : null;
}

function writeCookie(language) {
  // Not HttpOnly: this is a display preference the client sets and reads. It is
  // deliberately not on the API host, because it is not part of authentication.
  document.cookie = `${COOKIE}=${language}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
}

function fromBrowser() {
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const language = toLanguage(candidate);
    if (language) return language;
  }
  return null;
}

/**
 * Substitutes {name} placeholders.
 *
 * Named rather than positional: word order differs between Arabic and English,
 * and a positional placeholder silently produces nonsense when it moves.
 */
function interpolate(template, values) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match,
  );
}

export function LanguageProvider({ children, profileLanguage, onPersist }) {
  const [language, setLanguageState] = useState(
    () => readCookie() ?? fromBrowser() ?? DEFAULT_LANGUAGE,
  );

  // Adopt the profile language only when this device has expressed no
  // preference of its own.
  useEffect(() => {
    const profile = toLanguage(profileLanguage);
    if (profile && !readCookie()) setLanguageState(profile);
  }, [profileLanguage]);

  // The document element carries both, so CSS logical properties mirror and
  // screen readers announce the right language.
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = DIRECTIONS[language];
  }, [language]);

  const setLanguage = useCallback(
    (next) => {
      const chosen = toLanguage(next);
      if (!chosen) return;
      setLanguageState(chosen);
      writeCookie(chosen);
      // Persisting is best-effort: a failed save must not undo the switch the
      // user just made and can see.
      onPersist?.(chosen);
    },
    [onPersist],
  );

  const value = useMemo(() => {
    const table = TRANSLATIONS[language] ?? TRANSLATIONS[DEFAULT_LANGUAGE];

    /**
     * Looks up a key. A missing key falls back to the default language and then
     * to the key itself, so a gap shows up as a visible key rather than as an
     * empty screen.
     */
    const t = (key, values) => {
      const template = table[key] ?? TRANSLATIONS[DEFAULT_LANGUAGE][key] ?? key;
      return interpolate(template, values);
    };

    return { language, dir: DIRECTIONS[language], setLanguage, t };
  }, [language, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Access to the current language, its direction, the setter, and `t`. */
export function useI18n() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useI18n must be used inside a LanguageProvider');
  return context;
}

/**
 * Translates a server validation detail.
 *
 * The API returns a machine-readable `code` next to its human message. Without
 * that, an English-speaking parent would receive Arabic validation errors no
 * matter what the interface language says. The server's own message is the
 * fallback, so an unrecognised code still shows something useful.
 */
export function translateDetail(t, detail, values) {
  if (!detail) return '';
  if (detail.code) {
    const key = `valid.${detail.code}`;
    const translated = t(key, values);
    if (translated !== key) return translated;
  }
  return detail.message ?? '';
}
